import { useSyncExternalStore } from 'react';

/**
 * Light or dark: what this browser chose, or `auto` to follow the device's setting. The choice
 * is kept per browser (public/color-mode.js reads the same key before the page paints) and
 * shown as the `dark` or `light` class on <html>, which styles.css turns into the colors.
 * public/color-mode.js makes the same decision before the page paints; the two must agree.
 */

export type ColorMode = 'light' | 'dark' | 'auto';

const KEY = 'databink.color-mode';
const DEVICE = '(prefers-color-scheme: dark)';

function saved(): ColorMode {
  try {
    const mode = window.localStorage.getItem(KEY);
    return mode === 'light' || mode === 'dark' ? mode : 'auto';
  } catch {
    return 'auto';
  }
}

let mode: ColorMode = saved();
const device = window.matchMedia(DEVICE);
let dark = isDark();
const listeners = new Set<() => void>();

function isDark(): boolean {
  return mode === 'dark' || (mode === 'auto' && device.matches);
}

function apply(): void {
  dark = isDark();
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  root.classList.toggle('light', !dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
  for (const listener of listeners) listener();
}

export function setColorMode(next: ColorMode): void {
  mode = next;
  try {
    if (next === 'auto') window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, next);
  } catch {
    // Not remembered: this page still changes.
  }
  apply();
}

/** Follow the device while the choice is `auto`, and another tab's choice. Called once. */
export function watchColorMode(): void {
  device.addEventListener('change', () => {
    if (mode === 'auto') apply();
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY && event.key !== null) return;
    mode = saved();
    apply();
  });
  apply();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The choice, and whether the page is dark now. */
export function useColorMode(): { mode: ColorMode; dark: boolean } {
  return {
    mode: useSyncExternalStore(subscribe, () => mode),
    dark: useSyncExternalStore(subscribe, () => dark),
  };
}
